// src/redux/slice/subscription/subscriptionSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CommercialModel } from "../../../api/commercialModels/commercialModelsApiSlice";
import { LocationPros } from "../../../types/baseTypes";
import { SubscriptionSummary } from "types/bookingCreation";

export type SubscriptionPlan = "monthly" | "yearly";
export type SubscriptionFlowType = "existing" | "new" | null;

/**
 * Helper function to derive subscription type from CommercialModel
 * @param subscription - The selected subscription from API
 * @returns "monthly" | "yearly" | null
 */
export function getSubscriptionType(subscription: CommercialModel | null): SubscriptionPlan | null {
    if (!subscription) return null;
    const name = subscription.name.toLowerCase();
    if (name.includes("monthly")) return "monthly";
    if (name.includes("yearly") || name.includes("annual")) return "yearly";
    return null;
}

export interface SubscriptionState {
    // Flow routing
    hasExistingSubscription: SubscriptionFlowType;

    // Existing subscription flow (Flow 3)
    existingSubscription: {
        email: string | null;
        isVerified: boolean;
        verificationToken: string | null;
        customerAgreementId: string | null;
    };

    // New subscription flow (Flow 2)
    newSubscription: {
        isEligible: boolean | null;
        eligibilityReason: string | null;
        email: string | null;
        isEmailVerified: boolean;
        verificationCode: string | null;
        selectedLocation: LocationPros | null;
        selectedSubscriptionPlan: CommercialModel | null;
        hostData: {
            firstName: string | null;
            lastName: string | null;
            email: string | null;
            phone: string | null;
        } | null;
        paymentCompleted: boolean;
        subscriptionId: string | null;
        appliedVoucher: {
            voucherId: string;
            code: string;
            originalAmount: number;
            newAmount: number;
            discount: number;
        } | null;
        loading: {
            applyingVoucher: boolean;
        };
    };
    selectedSubscription: SubscriptionSummary | null;
}

const initialState: SubscriptionState = {
    hasExistingSubscription: null,

    existingSubscription: {
        email: null,
        isVerified: false,
        verificationToken: null,
        customerAgreementId: null,
    },

    newSubscription: {
        isEligible: null,
        eligibilityReason: null,
        email: null,
        isEmailVerified: false,
        verificationCode: null,
        selectedLocation: null,
        selectedSubscriptionPlan: null,
        hostData: null,
        paymentCompleted: false,
        subscriptionId: null,
        appliedVoucher: null,
        loading: {
            applyingVoucher: false,
        },
    },
    selectedSubscription: null,
};

const subscriptionSlice = createSlice({
    name: "subscription",
    initialState,
    reducers: {
        // Flow choice
        setHasExistingSubscription: (state, action: PayloadAction<SubscriptionFlowType>) => {
            state.hasExistingSubscription = action.payload;
        },

        // Existing subscription (Flow 3)
        setExistingSubscriptionEmail: (state, action: PayloadAction<string>) => {
            state.existingSubscription.email = action.payload;
        },
        setExistingSubscriptionVerified: (state, action: PayloadAction<boolean>) => {
            state.existingSubscription.isVerified = action.payload;
        },
        setExistingSubscriptionToken: (state, action: PayloadAction<string | null>) => {
            state.existingSubscription.verificationToken = action.payload;
        },
        setExistingSubscriptionCustomerAgreementId: (state, action: PayloadAction<string | null>) => {
            state.existingSubscription.customerAgreementId = action.payload;
        },

        // New subscription (Flow 2)
        setNewSubscriptionEligibility: (
            state,
            action: PayloadAction<{ isEligible: boolean; reason?: string }>
        ) => {
            state.newSubscription.isEligible = action.payload.isEligible;
            state.newSubscription.eligibilityReason = action.payload.reason || null;
        },
        setNewSubscriptionEmail: (state, action: PayloadAction<string>) => {
            state.newSubscription.email = action.payload;
        },
        setNewSubscriptionEmailVerified: (state, action: PayloadAction<boolean>) => {
            state.newSubscription.isEmailVerified = action.payload;
        },
        setNewSubscriptionVerificationCode: (state, action: PayloadAction<string>) => {
            state.newSubscription.verificationCode = action.payload;
        },
        setSelectedLocation: (state, action: PayloadAction<LocationPros | null>) => {
            state.newSubscription.selectedLocation = action.payload;
        },
        setSelectedSubscriptionPlan: (state, action: PayloadAction<CommercialModel>) => {
            state.newSubscription.selectedSubscriptionPlan = action.payload;
        },
        setNewSubscriptionHostData: (
            state,
            action: PayloadAction<SubscriptionState["newSubscription"]["hostData"]>
        ) => {
            state.newSubscription.hostData = action.payload;
        },
        setNewSubscriptionPaymentCompleted: (state, action: PayloadAction<boolean>) => {
            state.newSubscription.paymentCompleted = action.payload;
        },
        setNewSubscriptionId: (state, action: PayloadAction<string | null>) => {
            state.newSubscription.subscriptionId = action.payload;
        },

        // Voucher actions
        setAppliedVoucher: (
            state,
            action: PayloadAction<{
                voucherId: string;
                code: string;
                originalAmount: number;
                newAmount: number;
                discount: number;
            }>
        ) => {
            state.newSubscription.appliedVoucher = action.payload;
        },
        clearAppliedVoucher: (state) => {
            state.newSubscription.appliedVoucher = null;
        },
        setApplyingVoucher: (state, action: PayloadAction<boolean>) => {
            state.newSubscription.loading.applyingVoucher = action.payload;
        },

        // Reset
        resetSubscriptionFlow: () => initialState,
        setSelectedSubscription: (
            state,
            action: PayloadAction<SubscriptionSummary | null>
        ) => {
            state.selectedSubscription = action.payload;
        },
    },
});

export const {
    setHasExistingSubscription,
    setExistingSubscriptionEmail,
    setExistingSubscriptionVerified,
    setExistingSubscriptionToken,
    setExistingSubscriptionCustomerAgreementId,
    setNewSubscriptionEligibility,
    setNewSubscriptionEmail,
    setNewSubscriptionEmailVerified,
    setNewSubscriptionVerificationCode,
    setSelectedLocation,
    setSelectedSubscriptionPlan,
    setNewSubscriptionHostData,
    setNewSubscriptionPaymentCompleted,
    setNewSubscriptionId,
    setAppliedVoucher,
    clearAppliedVoucher,
    setApplyingVoucher,
    resetSubscriptionFlow,
    setSelectedSubscription,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
